/**
 * InstallPWABanner.jsx — Bannière proposant l'installation de Code 2 Roues comme app native.
 * Robuste : détecte le prompt natif si dispo (Chrome/Edge/Samsung), sinon affiche les
 * instructions manuelles adaptées à l'OS/navigateur (iOS Safari, Android Chrome, etc.).
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Smartphone, X, Download, HelpCircle } from 'lucide-react';

const DISMISS_KEY = 'c2r_pwa_dismissed_at';
const DISMISS_TTL_DAYS = 14;

function detectPlatform() {
  if (typeof window === 'undefined') return { os: 'unknown', browser: 'unknown' };
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isMac = /Macintosh/.test(ua) && !isIOS;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome|Chromium/.test(ua);
  const isChrome = /Chrome|Chromium|CriOS/.test(ua) && !/Edg|OPR/.test(ua);
  const isFirefox = /Firefox|FxiOS/.test(ua);
  const isEdge = /Edg/.test(ua);
  const os = isIOS ? 'ios' : isAndroid ? 'android' : isMac ? 'mac' : 'desktop';
  const browser = isSafari ? 'safari' : isChrome ? 'chrome' : isFirefox ? 'firefox' : isEdge ? 'edge' : 'other';
  return { os, browser };
}

function InstructionsBlock({ os, browser }) {
  if (os === 'ios') {
    return (
      <ol className="text-xs text-white/95 space-y-1.5 pl-4 list-decimal">
        <li>Appuie sur l&apos;icône <strong>Partager</strong> en bas de Safari (carré + flèche).</li>
        <li>Fais défiler et choisis <strong>« Sur l&apos;écran d&apos;accueil »</strong>.</li>
        <li>Confirme <strong>« Ajouter »</strong> en haut à droite.</li>
      </ol>
    );
  }
  if (os === 'android') {
    return (
      <ol className="text-xs text-white/95 space-y-1.5 pl-4 list-decimal">
        <li>Touche le menu <strong>⋮</strong> de Chrome (3 points en haut à droite).</li>
        <li>Choisis <strong>« Ajouter à l&apos;écran d&apos;accueil »</strong> ou <strong>« Installer l&apos;application »</strong>.</li>
        <li>Confirme <strong>« Ajouter »</strong>.</li>
      </ol>
    );
  }
  if (browser === 'chrome' || browser === 'edge') {
    return (
      <ol className="text-xs text-white/95 space-y-1.5 pl-4 list-decimal">
        <li>Clique sur l&apos;icône <strong>d&apos;installation</strong> dans la barre d&apos;adresse (à droite de l&apos;URL).</li>
        <li>Ou menu <strong>⋮</strong> → <strong>« Installer Code 2 Roues »</strong>.</li>
        <li>Confirme <strong>« Installer »</strong>.</li>
      </ol>
    );
  }
  return (
    <p className="text-xs text-white/95 leading-relaxed">
      Ton navigateur ne supporte pas l&apos;installation directe. Utilise Chrome, Edge ou Safari (iOS)
      pour installer Code 2 Roues comme une app.
    </p>
  );
}

export default function InstallPWABanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [platform] = useState(() => detectPlatform());
  const location = useLocation();

  // Routes critiques où la bannière masquerait des données importantes
  // (recherche annuaire, admin, formulaires RGPD). Sur ces routes on skip.
  const SKIP_PREFIXES = ['/annuaire', '/moto-ecoles', '/admin', '/mentions-annuaire'];
  const shouldSkipRoute = SKIP_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (shouldSkipRoute) { setVisible(false); return; }

    // Skip si déjà installé (standalone)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Skip si dismiss récent
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const then = parseInt(raw, 10);
        if (!Number.isNaN(then)) {
          const ageDays = (Date.now() - then) / (1000 * 60 * 60 * 24);
          if (ageDays < DISMISS_TTL_DAYS) return;
        }
      }
    } catch { /* ignore */ }

    // Chrome/Edge/Samsung → écouter beforeinstallprompt
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // Afficher la bannière après un délai raisonnable pour ne pas gêner
    const t = setTimeout(() => setVisible(true), 4000);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      clearTimeout(t);
    };
  }, [shouldSkipRoute]);

  // Réagir aux changements de route : masquer immédiatement si on entre
  // dans une route critique.
  useEffect(() => {
    if (shouldSkipRoute) setVisible(false);
  }, [shouldSkipRoute]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) {
      setShowInstructions(true);
      return;
    }
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* ignore */ }
    setDeferredPrompt(null);
    dismiss();
  };

  if (!visible || shouldSkipRoute) return null;

  const canPromptNative = !!deferredPrompt;
  const platformLabel =
    platform.os === 'ios' ? 'iPhone / iPad'
    : platform.os === 'android' ? 'Android'
    : platform.browser === 'chrome' || platform.browser === 'edge' ? 'Bureau'
    : 'Ton appareil';

  return (
    <div
      className="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-sm z-[60] rounded-2xl border-2 border-red-500/60 bg-gradient-to-br from-red-600 via-red-700 to-red-900 shadow-2xl p-4 animate-in slide-in-from-bottom-8 fade-in duration-500"
      data-testid="pwa-install-banner"
      role="dialog"
      aria-labelledby="pwa-install-title"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition"
        aria-label="Fermer"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div id="pwa-install-title" className="text-[10px] font-black uppercase tracking-[0.22em] text-white/80 mb-0.5">
            📱 Installe l&apos;app · {platformLabel}
          </div>
          <h3 className="text-base font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Code 2 Roues sur ton téléphone
          </h3>
          <p className="text-xs text-white/90 mt-1 leading-relaxed">
            Un accès direct depuis ton écran d&apos;accueil, plus rapide qu&apos;un site web.
          </p>
        </div>
      </div>

      {showInstructions && (
        <div className="mt-3 p-3 rounded-xl bg-black/25 border border-white/15 animate-in fade-in duration-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-white/80 mb-2">
            Comment installer sur {platformLabel}
          </div>
          <InstructionsBlock os={platform.os} browser={platform.browser} />
        </div>
      )}

      {canPromptNative ? (
        <button
          type="button"
          onClick={install}
          className="w-full mt-3 rounded-xl bg-white text-red-600 hover:bg-white/95 active:scale-[0.98] font-black uppercase tracking-widest py-3 text-sm inline-flex items-center justify-center gap-2 transition-all"
          data-testid="pwa-install-btn"
        >
          <Download className="w-4 h-4" /> Installer maintenant
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setShowInstructions((v) => !v)}
          className="w-full mt-3 rounded-xl bg-white text-red-600 hover:bg-white/95 active:scale-[0.98] font-black uppercase tracking-widest py-3 text-sm inline-flex items-center justify-center gap-2 transition-all"
          data-testid="pwa-install-btn"
        >
          <HelpCircle className="w-4 h-4" /> {showInstructions ? 'Masquer les étapes' : 'Voir les étapes'}
        </button>
      )}

      <button
        type="button"
        onClick={dismiss}
        className="w-full mt-2 text-xs font-bold text-white/70 hover:text-white py-1.5"
      >
        Plus tard
      </button>
    </div>
  );
}
